/**
 * Masking a bidder's product name for tomorrow's auction (`WEB-011`).
 *
 * Tomorrow's standings show that bidding is happening and at what level. They do not show
 * *who*, because a deal that has won nothing would otherwise collect buyer attention from
 * the board's own page — and attention on the board is precisely what canon § 7 and § 8
 * sell for a winning bid. Identity is earned by winning a position or by buying an
 * Honorable Mention, not by standing in the queue.
 *
 * The rule is written here rather than inline so it is one testable thing: first two
 * characters, `***`, last two characters.
 *
 * "Character" means what a reader would count, not what `String.prototype.slice` counts.
 * `"🚀🚀".slice(0, 2)` returns one rocket's worth of surrogate halves, and `Array.from`
 * — which iterates code points — still splits `🛰️` from the variation selector that makes
 * it an emoji. `Intl.Segmenter` at grapheme granularity is the unit a person perceives, and
 * it is the one that makes the four-character floor below mean what it says.
 */

const MASK = "***";

/** Below this the mask would print the whole name back. */
const MINIMUM_LENGTH = 5;

const graphemes = new Intl.Segmenter(undefined, { granularity: "grapheme" });

export function maskProductName(productName: string): string {
  const characters = [...graphemes.segment(productName)].map((piece) => piece.segment);
  if (characters.length < MINIMUM_LENGTH) return MASK;
  return `${characters.slice(0, 2).join("")}${MASK}${characters.slice(-2).join("")}`;
}
